import type { ColumnData, ColumnProp, ColumnRegular } from '@revolist/react-datagrid';

/** `displayName` → `display-name`: the column's own prop, in the shape the specs use. */
const toCellTestId = (prop: ColumnProp): string =>
  String(prop)
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .toLowerCase();

const withCellTestId = (column: ColumnRegular): ColumnRegular => {
  const testId = toCellTestId(column.prop);
  const { cellProperties } = column;

  return {
    ...column,
    cellProperties: (props) => ({ ...cellProperties?.(props), 'data-sm': testId }),
  };
};

/**
 * Stamps every leaf cell with `data-sm="<column prop, kebab-cased>"`, recursing
 * through the grouping nodes (a group is a header band, not data).
 *
 * A cell template renders *inside* the cell, so the id lands on the cell element
 * itself and holds whatever the column renders — plain text, a dropdown replica
 * or a toggle. `data-sm` is Playwright's `testIdAttribute` here, which makes a
 * cell `getByTestId('display-name')` within a row.
 *
 * Whatever a column already declares as `cellProperties` is kept and merged.
 */
export const withCellTestIds = (columns: ColumnData): ColumnData =>
  columns.map((column) =>
    'children' in column
      ? { ...column, children: withCellTestIds(column.children) }
      : withCellTestId(column)
  );
